import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from '../lib/icons';
import {
  fetchNavItems,
  activeHeaderItems,
  activeMoreItems,
  ctaItem,
  FALLBACK_NAV,
  type NavItem,
} from '../lib/nav-client';
import { SOCIAL_PLATFORMS } from '../data/social-config';

function YouTubeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
    </svg>
  );
}

function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z"/>
    </svg>
  );
}

// ── Nav link ──────────────────────────────────────────────────────────────────

function NavLink({ item, onClick }: { item: NavItem; onClick?: () => void }) {
  const location = useLocation();
  const active = location.pathname === item.route;
  const cls = `text-[#E0E0E0] hover:text-[#00BFFF] font-medium transition-colors duration-200 ${active ? 'text-[#00BFFF]' : ''}`;

  if (item.open_new_tab) {
    return (
      <a href={item.route} target="_blank" rel="noopener noreferrer" onClick={onClick} className={cls}>
        {item.label}
      </a>
    );
  }
  return (
    <Link to={item.route} onClick={onClick} className={cls}>
      {item.label}
    </Link>
  );
}

// ── More dropdown ─────────────────────────────────────────────────────────────

function MoreMenu({ items }: { items: NavItem[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Keyboard: Escape closes
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  if (items.length === 0) return null;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        className={`flex items-center gap-1 text-[#E0E0E0] hover:text-[#00BFFF] font-medium transition-colors duration-200 ${open ? 'text-[#00BFFF]' : ''}`}
      >
        More
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-52 bg-[#111] border border-gray-700 rounded-xl shadow-2xl shadow-black/60 py-1.5 z-50">
          {items.map(item => (
            <Link
              key={item.id}
              to={item.route}
              target={item.open_new_tab ? '_blank' : undefined}
              rel={item.open_new_tab ? 'noopener noreferrer' : undefined}
              onClick={() => setOpen(false)}
              className="block px-4 py-2.5 text-sm text-[#E0E0E0] hover:text-[#00BFFF] hover:bg-gray-800/60 transition-colors duration-150"
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Mobile nav ────────────────────────────────────────────────────────────────

function MobileNav({
  headerItems,
  moreItems,
  cta,
  onClose,
}: {
  headerItems: NavItem[];
  moreItems: NavItem[];
  cta: NavItem | undefined;
  onClose: () => void;
}) {
  return (
    <div className="md:hidden py-4 border-t border-gray-800">
      <nav className="flex flex-col space-y-1">
        {headerItems.map(item => (
          <Link
            key={item.id}
            to={item.route}
            onClick={onClose}
            target={item.open_new_tab ? '_blank' : undefined}
            rel={item.open_new_tab ? 'noopener noreferrer' : undefined}
            className="text-[#E0E0E0] hover:text-[#00BFFF] font-medium transition-colors duration-200 px-2 py-2.5 rounded-lg hover:bg-gray-800/40"
          >
            {item.label}
          </Link>
        ))}

        {moreItems.length > 0 && (
          <>
            <div className="flex items-center gap-3 py-2 px-2">
              <div className="flex-1 h-px bg-gray-800" />
              <span className="text-[10px] uppercase tracking-widest text-gray-600 font-semibold">More</span>
              <div className="flex-1 h-px bg-gray-800" />
            </div>
            {moreItems.map(item => (
              <Link
                key={item.id}
                to={item.route}
                onClick={onClose}
                target={item.open_new_tab ? '_blank' : undefined}
                rel={item.open_new_tab ? 'noopener noreferrer' : undefined}
                className="text-gray-400 hover:text-[#E0E0E0] font-medium transition-colors duration-200 px-2 py-2.5 rounded-lg hover:bg-gray-800/40"
              >
                {item.label}
              </Link>
            ))}
          </>
        )}

        {/* Follow section */}
        {(SOCIAL_PLATFORMS.youtube.url || SOCIAL_PLATFORMS.tiktok.url) && (
          <>
            <div className="flex items-center gap-3 py-2 px-2">
              <div className="flex-1 h-px bg-gray-800" />
              <span className="text-[10px] uppercase tracking-widest text-gray-600 font-semibold">Follow GameFix</span>
              <div className="flex-1 h-px bg-gray-800" />
            </div>
            <div className="flex items-center gap-4 px-2 py-1">
              {SOCIAL_PLATFORMS.youtube.url && (
                <a href={SOCIAL_PLATFORMS.youtube.url} target="_blank" rel="noopener noreferrer" aria-label="YouTube"
                  onClick={onClose}
                  className="flex items-center gap-2 text-gray-400 hover:text-[#FF0000] transition-colors duration-200 font-medium">
                  <YouTubeIcon className="h-5 w-5" />
                  <span className="text-sm">YouTube</span>
                </a>
              )}
              {SOCIAL_PLATFORMS.tiktok.url && (
                <a href={SOCIAL_PLATFORMS.tiktok.url} target="_blank" rel="noopener noreferrer" aria-label="TikTok"
                  onClick={onClose}
                  className="flex items-center gap-2 text-gray-400 hover:text-[#E0E0E0] transition-colors duration-200 font-medium">
                  <TikTokIcon className="h-5 w-5" />
                  <span className="text-sm">TikTok</span>
                </a>
              )}
            </div>
          </>
        )}

        {cta && (
          <div className="pt-3">
            <Link
              to={cta.route}
              onClick={onClose}
              className="block bg-[#39FF14] hover:bg-[#00BFFF] text-[#0D0D0D] font-bold px-4 py-3 rounded-xl transition-colors duration-200 text-center"
            >
              {cta.label}
            </Link>
          </div>
        )}
      </nav>
    </div>
  );
}

// ── Main header ───────────────────────────────────────────────────────────────

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [navItems, setNavItems] = useState<NavItem[]>(FALLBACK_NAV);
  const location = useLocation();

  // Load nav config on mount
  useEffect(() => {
    fetchNavItems().then(setNavItems).catch(() => {/* keep fallback */});
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const headerItems = activeHeaderItems(navItems);
  const moreItems   = activeMoreItems(navItems);
  const cta         = ctaItem(navItems);

  const closeMobile = useCallback(() => setMenuOpen(false), []);

  return (
    <header className="bg-[#0D0D0D] shadow-lg sticky top-0 z-50 border-b border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ── Desktop layout (md+) ── */}
        <div className="hidden md:flex justify-between items-center py-4">
          <Link to="/" className="flex items-center flex-shrink-0">
            <img src="/IMG_8439.JPG" alt="GameFix Solutions" className="h-16 object-contain" />
          </Link>
          <nav className="flex items-center gap-7">
            {headerItems.map(item => (
              <NavLink key={item.id} item={item} />
            ))}
            <MoreMenu items={moreItems} />
            <div className="flex items-center gap-3 ml-1">
              {SOCIAL_PLATFORMS.youtube.url && (
                <a href={SOCIAL_PLATFORMS.youtube.url} target="_blank" rel="noopener noreferrer" aria-label="YouTube"
                  className="text-[#B0B0B0] hover:text-[#FF0000] transition-colors duration-200">
                  <YouTubeIcon className="h-5 w-5" />
                </a>
              )}
              {SOCIAL_PLATFORMS.tiktok.url && (
                <a href={SOCIAL_PLATFORMS.tiktok.url} target="_blank" rel="noopener noreferrer" aria-label="TikTok"
                  className="text-[#B0B0B0] hover:text-[#E0E0E0] transition-colors duration-200">
                  <TikTokIcon className="h-5 w-5" />
                </a>
              )}
            </div>
            {cta && (
              <Link
                to={cta.route}
                className="bg-[#39FF14] hover:bg-[#00BFFF] text-[#0D0D0D] font-bold px-4 py-2 rounded-lg transition-colors duration-200 whitespace-nowrap ml-2"
              >
                {cta.label}
              </Link>
            )}
          </nav>
        </div>

        {/* ── Mobile layout (<md) — two rows ── */}
        <div className="md:hidden">
          {/* Row 1: Logo + Hamburger */}
          <div className="flex justify-between items-center py-2">
            <Link to="/" className="flex items-center flex-shrink-0">
              <img src="/IMG_8439.JPG" alt="GameFix Solutions" className="h-12 object-contain" />
            </Link>
            <button
              onClick={() => setMenuOpen(o => !o)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              className="p-2 rounded-lg hover:bg-gray-800 transition-colors duration-200 text-[#E0E0E0]"
            >
              {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
          {/* Row 2: Social icons (left) + Book Repair button (right) */}
          <div className="flex justify-between items-center pb-2.5">
            <div className="flex items-center gap-3">
              {SOCIAL_PLATFORMS.youtube.url && (
                <a href={SOCIAL_PLATFORMS.youtube.url} target="_blank" rel="noopener noreferrer" aria-label="YouTube"
                  className="text-[#B0B0B0] hover:text-[#FF0000] transition-colors duration-200 p-1">
                  <YouTubeIcon className="h-5 w-5" />
                </a>
              )}
              {SOCIAL_PLATFORMS.tiktok.url && (
                <a href={SOCIAL_PLATFORMS.tiktok.url} target="_blank" rel="noopener noreferrer" aria-label="TikTok"
                  className="text-[#B0B0B0] hover:text-[#E0E0E0] transition-colors duration-200 p-1">
                  <TikTokIcon className="h-5 w-5" />
                </a>
              )}
            </div>
            {cta && (
              <Link
                to={cta.route}
                className="bg-[#39FF14] hover:bg-[#00BFFF] text-[#0D0D0D] font-bold px-4 py-2 rounded-lg transition-colors duration-200 whitespace-nowrap text-sm"
              >
                {cta.label}
              </Link>
            )}
          </div>
        </div>

        {/* Mobile menu dropdown */}
        {menuOpen && (
          <MobileNav
            headerItems={headerItems}
            moreItems={moreItems}
            cta={cta}
            onClose={closeMobile}
          />
        )}
      </div>
    </header>
  );
};

export default Header;
